
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, useSpring, useTransform } from 'framer-motion';
import { Wallet, LogOut, ShoppingBag, List, Shield, User as UserIcon, Loader2, Zap, LayoutDashboard, WifiOff, Gamepad2, Wrench, Hammer, Construction } from 'lucide-react';
import { User, Tab, Product, Wallet as WalletType } from './types';
import { getCurrentUserProfile, getEmailByUsername, getWallets } from './services/storage';
import { supabase } from './supabaseClient';
import { Skeleton } from './components/Skeleton';

import Shop from './components/Shop';
import WalletHistory from './components/WalletHistory';
import AdminPanel from './components/AdminPanel';
import Checkout from './components/Checkout';
import Dashboard from './components/Dashboard';
import ErrorBoundary from './components/ErrorBoundary';

// --- Custom Hook: useAdmin ---
const useAdmin = (user: User | null) => {
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (user && (user.role === 'admin' || user.is_admin === true)) {
      setIsAdmin(true);
    } else {
      setIsAdmin(false);
    }
  }, [user]);

  return isAdmin;
};

// --- Animated Counter ---
const AnimatedCounter = ({ value }: { value: number }) => {
  const spring = useSpring(value, { mass: 0.8, stiffness: 75, damping: 15 });
  const display = useTransform(spring, (current) => current.toFixed(2));

  useEffect(() => {
    spring.set(value);
  }, [value, spring]);

  return <motion.span className="font-mono">{display}</motion.span>;
};

// --- Placeholder Components ---
const ToolsPlaceholder = () => (
  <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-6 animate-fade-in">
    <motion.div 
      animate={{ rotate: [0, 10, -10, 0] }}
      transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
      className="w-24 h-24 bg-zinc-900 rounded-3xl border border-accent-500/30 flex items-center justify-center mb-6 shadow-neon"
    >
      <Wrench className="text-accent-500" size={48} />
    </motion.div>
    <h2 className="text-3xl font-bold text-white mb-2 tracking-tight">Utility Hub</h2>
    <p className="text-zinc-500 font-mono text-xs uppercase tracking-widest mb-6">System Modules compiling...</p>
    <div className="bg-zinc-900/50 border border-white/5 p-4 rounded-xl max-w-xs w-full">
        <div className="flex items-center gap-3 mb-2">
            <Construction size={16} className="text-zinc-500" />
            <span className="text-xs text-white font-bold">In Development</span>
        </div>
        <div className="w-full bg-black h-1.5 rounded-full overflow-hidden">
            <motion.div 
                initial={{ width: "0%" }}
                animate={{ width: "65%" }}
                transition={{ duration: 1.5, ease: "easeOut" }}
                className="h-full bg-accent-500 rounded-full"
            />
        </div>
    </div>
  </div>
);

const GamesPlaceholder = () => (
  <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-6 animate-fade-in">
    <motion.div 
      animate={{ y: [0, -10, 0] }}
      transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
      className="w-24 h-24 bg-gradient-to-br from-purple-900/20 to-blue-900/20 rounded-3xl border border-purple-500/30 flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(168,85,247,0.2)]"
    >
      <Gamepad2 className="text-purple-400" size={48} />
    </motion.div>
    <h2 className="text-3xl font-bold text-white mb-2 tracking-tight">Puchi Arcade</h2>
    <p className="text-zinc-500 font-mono text-xs uppercase tracking-widest mb-6">Initializing Game Engine...</p>
    
    <div className="grid grid-cols-2 gap-3 max-w-sm w-full">
       <div className="bg-zinc-900/50 border border-white/5 p-4 rounded-xl flex flex-col items-center gap-2 opacity-50">
           <div className="w-8 h-8 rounded-full bg-zinc-800"></div>
           <div className="w-16 h-2 bg-zinc-800 rounded"></div>
       </div>
       <div className="bg-zinc-900/50 border border-white/5 p-4 rounded-xl flex flex-col items-center gap-2 opacity-50">
           <div className="w-8 h-8 rounded-full bg-zinc-800"></div>
           <div className="w-16 h-2 bg-zinc-800 rounded"></div>
       </div>
    </div>
    <div className="mt-8 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full text-[10px] text-purple-400 font-bold uppercase tracking-wider animate-pulse">
        Coming Soon
    </div>
  </div>
);

// --- Auth Component ---
const Auth: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        let emailToUse = email;
        if (!emailToUse) {
           const lookedUpEmail = await getEmailByUsername(username);
           if (!lookedUpEmail) throw new Error('User not found. Please create an account.');
           emailToUse = lookedUpEmail;
        }

        const { error } = await supabase.auth.signInWithPassword({
          email: emailToUse,
          password
        });
        if (error) throw error;

      } else {
        const { data: authData, error: authError } = await supabase.auth.signUp({ 
          email, 
          password,
          options: {
            data: { username }
          }
        });
        if (authError) throw authError;

        if (authData.user) {
          const { error: profileError } = await supabase
            .from('profiles')
            .insert([{
              id: authData.user.id,
              username,
              email,
              role: 'student',
              balance: 0,
              created_at: new Date().toISOString()
            }]);
          
          if (profileError && !profileError.message.includes('duplicate key')) {
             console.warn("Manual profile creation failed", profileError);
          }
        }
      }
    } catch (err: any) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-main p-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-zinc-900/50 via-bg-main to-bg-main pointer-events-none" />
      
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="relative z-10 w-full max-w-sm glass-panel rounded-3xl shadow-2xl overflow-hidden p-8 border border-white/5"
      >
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 bg-zinc-900 rounded-2xl border border-accent-500/30 flex items-center justify-center shadow-neon mb-4">
            <Zap className="text-accent-500 fill-accent-500/20" size={28} />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Puchi Wallet</h1>
          <p className="text-zinc-500 text-xs mt-1 font-mono tracking-widest uppercase">Secure Access V2</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <input
              type="text"
              placeholder="Username"
              className="w-full bg-black/40 border border-white/10 text-white rounded-xl px-4 py-3.5 focus:border-accent-500 focus:shadow-neon transition-all outline-none font-mono text-sm placeholder:text-zinc-600"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>
          
          {!isLogin && (
             <input
              type="email"
              placeholder="Email Address"
              className="w-full bg-black/40 border border-white/10 text-white rounded-xl px-4 py-3.5 focus:border-accent-500 focus:shadow-neon transition-all outline-none font-mono text-sm placeholder:text-zinc-600"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          )}

          <div className="space-y-1">
             <input
              type="password"
              placeholder="Password"
              className="w-full bg-black/40 border border-white/10 text-white rounded-xl px-4 py-3.5 focus:border-accent-500 focus:shadow-neon transition-all outline-none font-mono text-sm placeholder:text-zinc-600"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          {error && <p className="text-red-400 text-xs text-center bg-red-900/10 p-2 rounded border border-red-900/30">{error}</p>}
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-accent-500 hover:bg-accent-400 text-black font-bold py-4 rounded-xl transition-all active:scale-95 shadow-neon hover:shadow-neon-strong uppercase tracking-wider text-xs mt-2"
          >
            {loading ? <Loader2 className="animate-spin mx-auto" /> : (isLogin ? 'Enter Wallet' : 'Initialize Account')}
          </button>
        </form>

        <div className="mt-6 pt-6 border-t border-white/5 text-center">
          <button
            onClick={() => { setIsLogin(!isLogin); setError(''); }}
            className="text-xs text-zinc-500 hover:text-accent-500 transition-colors uppercase tracking-widest"
          >
            {isLogin ? "Register Account" : "Login"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// --- Main App ---

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [authUser, setAuthUser] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  const [checkingSession, setCheckingSession] = useState(true);
  const [isOffline, setIsOffline] = useState(false);
  const [activeWallet, setActiveWallet] = useState<WalletType | null>(null);
  const [checkoutProduct, setCheckoutProduct] = useState<Product | null>(null);

  const isAdmin = useAdmin(user);

  const syncUser = useCallback(async (userId: string) => {
    try {
      const profile = await getCurrentUserProfile(userId);
      if (profile) {
        setUser(profile);
        setIsOffline(false);
        const storedWalletId = localStorage.getItem('active_wallet_id') || 'main';
        const wallets = await getWallets(userId);
        const selected = wallets.find(w => w.id === storedWalletId) || wallets.find(w => w.id === 'main');
        if (selected) setActiveWallet(selected);
      }
    } catch (e) {
      console.warn("Sync failed", e);
      setIsOffline(true);
    }
  }, []);

  useEffect(() => {
    const initSession = async () => {
      const loadingGuard = setTimeout(() => { if (checkingSession) setCheckingSession(false); }, 4000);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setAuthUser(session.user);
          await syncUser(session.user.id);
        }
      } catch (err) {
        console.error("Init Error:", err);
      } finally {
        setCheckingSession(false);
        clearTimeout(loadingGuard);
      }
    };
    initSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session) {
        setAuthUser(session.user);
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') await syncUser(session.user.id);
      } else {
        setAuthUser(null);
        setUser(null);
        setActiveWallet(null);
      }
    });
    return () => subscription.unsubscribe();
  }, [syncUser]);

  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase.channel(`realtime:profile:${user.id}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${user.id}` }, 
        (payload) => {
           if (payload.new) {
               setUser(prev => prev ? { ...prev, ...payload.new } : null);
               if (activeWallet?.id === 'main') setActiveWallet(prev => prev ? { ...prev, balance: payload.new.balance } : null);
           }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') setIsOffline(false);
        else if (status === 'CHANNEL_ERROR') setIsOffline(true);
      });
    return () => { supabase.removeChannel(channel); };
  }, [user?.id, activeWallet?.id]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setAuthUser(null);
    setCheckingSession(false);
    setActiveTab('shop'); 
    localStorage.removeItem('active_wallet_id');
  };

  const updateCurrentUser = (updated: User) => {
    setUser(updated);
    if (activeWallet?.id === 'main') setActiveWallet(prev => prev ? { ...prev, balance: updated.balance } : null);
  };

  const initiateCheckout = (product: Product) => {
    setCheckoutProduct(product);
    setActiveTab('checkout');
  };

  const handleCheckoutSuccess = (updatedUser: User) => {
    updateCurrentUser(updatedUser);
    setCheckoutProduct(null);
    setActiveTab('history');
  };

  if (!checkingSession && !authUser) return <ErrorBoundary><Auth /></ErrorBoundary>;

  const displayBalance = activeWallet?.balance ?? 0;

  // Navigation config
  const navItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Wallet' },
    { id: 'shop', icon: ShoppingBag, label: 'Market' },
    { id: 'history', icon: List, label: 'Logbook' },
    { id: 'tools', icon: Wrench, label: 'Tools' },
    { id: 'games', icon: Gamepad2, label: 'Games' },
    ...(isAdmin ? [{ id: 'admin', icon: Shield, label: 'Console' }] : [])
  ];

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-bg-main text-zinc-100 flex flex-col font-sans selection:bg-accent-500 selection:text-white pb-24 md:pb-0">
        
        {/* --- MOBILE HEADER (Sticky Top) --- */}
        <div className="md:hidden sticky top-0 z-40 glass-panel border-b border-white/5 p-4 flex justify-between items-center h-[64px]">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-accent-500/10 flex items-center justify-center border border-accent-500/20">
                 <Zap className="text-accent-500 fill-accent-500/20" size={16} />
              </div>
              <span className="font-bold text-lg tracking-tight text-white">Puchi</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-zinc-900 px-3 py-1.5 rounded-lg border border-white/10 flex items-center gap-2 shadow-inner">
                <span className="text-zinc-500 text-[10px] font-bold uppercase">$</span>
                <span className="text-white font-mono font-bold text-sm">
                  {user ? <AnimatedCounter value={displayBalance} /> : <Skeleton className="w-8 h-4"/>}
                </span>
              </div>
              <button onClick={handleLogout} className="text-zinc-500 hover:text-red-500 transition-colors p-2">
                <LogOut size={18} />
              </button>
            </div>
        </div>

        {/* --- DESKTOP SIDEBAR --- */}
        <aside className="hidden md:fixed md:left-0 md:top-0 md:h-full md:w-64 bg-bg-card border-r border-white/5 z-50 flex-col p-6 hidden md:flex">
             <div className="flex items-center gap-3 mb-12">
                <div className="w-10 h-10 bg-accent-500 rounded-xl flex items-center justify-center shadow-neon">
                  <Zap className="text-black fill-black/20" size={20} />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-white leading-none">Puchi Wallet</h1>
                  <span className="text-[10px] text-zinc-500 font-mono tracking-widest uppercase">Luxury V2</span>
                </div>
             </div>

             <div className="bg-gradient-to-br from-zinc-900 to-black border border-accent-500/20 p-5 rounded-2xl mb-8 relative overflow-hidden group">
                 <div className="absolute top-0 right-0 w-32 h-32 bg-accent-500/5 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2 group-hover:bg-accent-500/10 transition-colors"></div>
                 <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-1">Total Balance</p>
                 <div className="flex items-baseline gap-1">
                    <span className="text-xl text-accent-500 font-bold">$</span>
                    <span className="text-3xl font-bold text-white tracking-tight">
                       {user ? <AnimatedCounter value={displayBalance} /> : <Skeleton className="h-8 w-24"/>}
                    </span>
                 </div>
             </div>

             <nav className="flex-1 space-y-2">
                 {navItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => setActiveTab(item.id as Tab)}
                      className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all duration-300 group ${
                        activeTab === item.id 
                        ? 'bg-accent-500 text-black font-bold shadow-neon' 
                        : 'text-zinc-500 hover:text-white hover:bg-white/5'
                      }`}
                    >
                       <item.icon size={20} />
                       <span className="text-sm">{item.label}</span>
                    </button>
                 ))}
             </nav>

             <div className="pt-6 border-t border-white/5">
                <div className="flex items-center gap-3 mb-4 px-2">
                    <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center border border-white/10">
                      <UserIcon size={14} className="text-zinc-400"/>
                    </div>
                    <div className="overflow-hidden">
                      <p className="text-sm font-bold text-white truncate">{user?.username || 'Guest'}</p>
                      <p className="text-[10px] text-accent-500 uppercase tracking-wider">{isAdmin ? 'Admin' : 'Student'}</p>
                    </div>
                </div>
                <button onClick={handleLogout} className="w-full flex items-center gap-2 text-zinc-500 hover:text-red-500 transition-colors px-2 py-2 text-xs font-mono uppercase tracking-wider hover:bg-red-500/5 rounded">
                  <LogOut size={14} /> Disconnect
                </button>
             </div>
        </aside>

        {/* --- MAIN CONTENT AREA --- */}
        <main className="flex-1 md:ml-64 relative bg-bg-main overflow-x-hidden">
           <AnimatePresence>
             {isOffline && (
               <motion.div initial={{ y: -50 }} animate={{ y: 0 }} exit={{ y: -50 }} className="fixed top-20 right-4 z-[100] bg-red-900/80 backdrop-blur border border-red-500/30 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 text-xs font-mono">
                 <WifiOff size={14} /> OFFLINE MODE
               </motion.div>
             )}
           </AnimatePresence>

           <div className="p-4 md:p-10 max-w-7xl mx-auto min-h-[calc(100vh-80px)]">
              <AnimatePresence mode="wait">
                 {user ? (
                    <motion.div 
                        key={activeTab} 
                        initial={{ opacity: 0, x: 20 }} 
                        animate={{ opacity: 1, x: 0 }} 
                        exit={{ opacity: 0, x: -20 }} 
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    >
                        {activeTab === 'dashboard' && <Dashboard currentUser={user} activeWallet={activeWallet} setActiveWallet={setActiveWallet} onBalanceUpdate={updateCurrentUser} onNavigate={setActiveTab} />}
                        {activeTab === 'shop' && <Shop currentUser={user} onBalanceUpdate={updateCurrentUser} onProductSelect={initiateCheckout} />}
                        {activeTab === 'checkout' && checkoutProduct && activeWallet && <Checkout product={checkoutProduct} user={user} activeWallet={activeWallet} onSuccess={handleCheckoutSuccess} onBack={() => setActiveTab('shop')} />}
                        {activeTab === 'history' && <WalletHistory currentUser={user} />}
                        {activeTab === 'tools' && <ToolsPlaceholder />}
                        {activeTab === 'games' && <GamesPlaceholder />}
                        {activeTab === 'admin' && isAdmin && <AdminPanel />}
                    </motion.div>
                 ) : (
                    <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-accent-500" /></div>
                 )}
              </AnimatePresence>
           </div>
        </main>

        {/* --- MOBILE BOTTOM NAV (Glassmorphism + Animations) --- */}
        <nav className="md:hidden fixed bottom-4 left-4 right-4 h-[70px] glass-panel rounded-2xl border border-white/10 z-50 flex justify-between items-center px-2 shadow-2xl">
            {navItems.slice(0, 5).map((item) => ( // Limit to 5 items max for mobile spacing
                <motion.button
                    key={item.id}
                    onClick={() => setActiveTab(item.id as Tab)}
                    whileTap={{ scale: 0.9 }}
                    className={`relative flex flex-col items-center justify-center w-full h-full rounded-xl transition-colors ${
                        activeTab === item.id ? 'text-accent-500' : 'text-zinc-500'
                    }`}
                >
                    <div className={`relative p-2 rounded-xl transition-all duration-300 ${activeTab === item.id ? 'bg-accent-500/10 -translate-y-2 shadow-lg shadow-accent-500/20 border border-accent-500/20' : ''}`}>
                       <item.icon size={22} strokeWidth={activeTab === item.id ? 2.5 : 2} />
                       {activeTab === item.id && (
                           <motion.div 
                             layoutId="mobile-nav-dot" 
                             className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-1 h-1 bg-accent-500 rounded-full"
                           />
                       )}
                    </div>
                    {/* Optional Label for better UX on larger phones, or hide on small */}
                    <span className={`text-[9px] font-bold uppercase tracking-wider mt-1 transition-all ${activeTab === item.id ? 'opacity-100 translate-y-[-4px]' : 'opacity-0 translate-y-2 hidden'}`}>
                        {item.label}
                    </span>
                </motion.button>
            ))}
            {/* If Admin exists, maybe show a small distinct indicator or swap games? 
                For now slicing at 5 keeps UI clean. Admin can access via sidebar on desktop or we add a 'More' menu later. 
                But prompt implies simple addition. Let's strictly follow the 'below logbook' which puts them in slot 4 & 5. 
            */}
        </nav>

      </div>
    </ErrorBoundary>
  );
};

export default App;
